import { ConversationMessages } from "@/components/ConversationMessages";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useGetConversationsQuery,
  useMarkAsReadMutation,
} from "@/store/conversations/apis";
import { getSelectedMover, setSelectedMover } from "@/store/mover/slice";
import { ChevronRightIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

const Conversations = () => {
  const dispatch = useDispatch();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [markAsReadApi] = useMarkAsReadMutation();
  const selectedMover = useSelector(getSelectedMover);

  const [page, setPage] = useState(1);

  const {
    data: conversations,
    isLoading,
    refetch,
  } = useGetConversationsQuery(
    { page, limit: 25 },
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (!conversations) return;

    // Select first conversation when data is first loaded
    if (conversations.length > 0 && !selectedIndex) {
      // setSelectedIndex(conversations[0].id);
    }
  }, [conversations, selectedIndex]);

  const handleMarkAsRead = async (customerId: number) => {
    await markAsReadApi({ customerId });
    refetch();
    const mover = Object.assign({}, selectedMover);
    mover.unread_conversation_count -= 1;
    dispatch(setSelectedMover(mover));
  };

  const selectedConversation = useMemo(() => {
    return conversations?.find((conv) => conv.id === selectedIndex);
  }, [conversations, selectedIndex]);

  const handleScroll = useCallback(
    (e: any) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const scrollThreshold = scrollHeight - clientHeight;

      if (scrollTop > scrollThreshold / 2 && !isLoading) {
        setPage((prev) => prev + 1);
      }
    },
    [isLoading]
  );

  if (!conversations) return null;

  return (
    <div className="h-[calc(100vh-152px)] flex px-4">
      {/* Left sidebar with conversations list */}
      <div className="w-1/4 border-r h-full -ml-4">
        {conversations.length > 0 ? (
          <ScrollArea className="h-full" onScroll={handleScroll}>
            {conversations.map((conv) => (
              <div
                key={conv.id}
                className={`w-full flex justify-between px-4 py-2 hover:bg-stone-100 cursor-pointer ${
                  selectedConversation?.id === conv.id
                    ? "bg-stone-50 font-semibold"
                    : ""
                }`}
                onClick={() => {
                  setSelectedIndex(conv.id);
                  handleMarkAsRead(conv.id);
                }}
              >
                <span className="text-left">{conv.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-right">
                    {conv.unread_messages ? (
                      <Badge
                        variant={"default"}
                        className="font-medium bg-red-400 text-white px-2"
                      >
                        NEW
                      </Badge>
                    ) : null}
                  </span>
                  <span>
                    {selectedConversation?.id === conv.id ? (
                      <ChevronRightIcon className="w-4 h-4 font-bold" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4 text-stone-200" />
                    )}
                  </span>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="p-4 text-sm text-muted-foreground">
                Loading...
              </div>
            )}
          </ScrollArea>
        ) : (
          <div className="text-black p-4">No messages found</div>
        )}
      </div>

      <div className="w-3/4 h-full">
        <div className="mx-auto w-full h-full">
          {selectedConversation ? (
            <ConversationMessages
              messages={selectedConversation.messages}
              customer={selectedConversation}
              onMessageSent={refetch}
              moveId={selectedConversation.most_recent_move?.id}
              showHeader={true}
            />
          ) : (
            <div className="text-lg flex flex-col items-center justify-start h-full pt-4">
              {conversations.length === 0
                ? "Messages (texts and emails) from customers will appear here."
                : "Select a customer to view messages"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Conversations;
